import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_CONFIG } from '@/config/app-config';

export const OrganizationSEO: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Remove any existing organization structured data
    const existingScript = document.querySelector('script[data-organization-structured-data="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    const isZh = i18n.language === 'zh';

    // Create organization structured data
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Resonow",
      "alternateName": isZh ? "共振整合疗愈平台" : "Resonow Integrative Therapy Platform",
      "description": isZh 
        ? "Resonow是一站式整合疗愈平台，融合临床心理学与身心疗愈，通过AI智能匹配和150+疗法数据库，精准连接持证咨询师与整合疗愈师。"
        : "Resonow is a one-stop integrative therapy platform bridging clinical psychology and holistic healing, powered by AI-driven matching and a 150+ therapy modality database.",
      "url": APP_CONFIG.PRODUCTION_DOMAIN,
      "logo": `${APP_CONFIG.PRODUCTION_DOMAIN}/logo.png`,
      "image": `${APP_CONFIG.PRODUCTION_DOMAIN}/images/resonow-hero.jpg`,
      "foundingDate": "2023",
      "foundingLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "425 Washington Blvd",
          "addressLocality": "Jersey City",
          "addressRegion": "NJ",
          "postalCode": "07310",
          "addressCountry": "US"
        }
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "425 Washington Blvd",
        "addressLocality": "Jersey City",
        "addressRegion": "NJ",
        "postalCode": "07310",
        "addressCountry": "US"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "telephone": "+1-909-905-7788",
          "email": "info@resonow.org",
          "availableLanguage": ["en", "zh"]
        },
        {
          "@type": "ContactPoint",
          "contactType": "technical support",
          "email": "info@resonow.org",
          "availableLanguage": ["en", "zh"]
        }
      ],
      "sameAs": [
        "http://linkedin.com/company/resonow",
        "https://www.xiaohongshu.com/user/profile/63e3d3dd0000000027028c96"
      ],
      "knowsAbout": [
        isZh ? "整合心理学" : "Integrative Psychology",
        isZh ? "整合疗愈" : "Integrative Healing", 
        isZh ? "身心疗法" : "Mind-Body Therapy",
        isZh ? "替代疗法" : "Alternative Therapy",
        isZh ? "心理咨询" : "Psychological Counseling",
        isZh ? "整体健康" : "Holistic Health",
        isZh ? "数据驱动疗法匹配" : "Data-Driven Therapy Matching"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": isZh ? "身心健康服务" : "Integrative Wellness Services",
        "itemListElement": [
          {
            "@type": "OfferCatalog",
            "name": isZh ? "心理咨询" : "Psychological Counseling",
            "description": isZh ? "专业心理健康咨询服务" : "Professional mental health counseling services"
          },
          {
            "@type": "OfferCatalog", 
            "name": isZh ? "整合疗法" : "Integrative Therapy",
            "description": isZh ? "身心整合疗法服务" : "Mind-body integrative therapy services"
          },
          {
            "@type": "OfferCatalog",
            "name": isZh ? "替代疗法" : "Alternative Therapy", 
            "description": isZh ? "专业替代疗法和身心健康支持" : "Professional alternative therapy and holistic wellness support"
          }
        ]
      },
      "makesOffer": {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": isZh ? "专业疗愈服务预约" : "Professional Healing Service Booking",
          "description": isZh ? "在线预约专业认证的疗愈师" : "Online booking with certified professional healers"
        },
        "availability": "https://schema.org/InStock",
        "areaServed": {
          "@type": "Place",
          "name": isZh ? "全球" : "Global"
        },
        "availableLanguage": ["en", "zh"]
      }
    };

    // Add the structured data to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-organization-structured-data', 'true');
    script.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.querySelector('script[data-organization-structured-data="true"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [i18n.language]);

  return null;
};